import { memo, useCallback, useState, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/react';
import { IconFlag, IconForward, IconPin, IconReply } from '../icons';

type MessageActionMenuProps = {
  children: ReactNode;
  timestampLabel: string;
  isPinned: boolean;
  onReply?: () => void;
  onTogglePin?: () => void;
  onForward?: () => void;
  onReport?: () => void;
};

type ActionItemProps = {
  label: string;
  icon: ReactNode;
  tone?: 'default' | 'danger';
  onPress: () => void;
};

function ActionItem({ label, icon, tone = 'default', onPress }: ActionItemProps) {
  return (
    <button
      type="button"
      className={[
        'flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left text-sm font-medium transition-colors',
        tone === 'danger'
          ? 'text-[#D13939] hover:bg-[#FFF1F1]'
          : 'text-[#1E1E1E] hover:bg-black/[0.04]',
      ].join(' ')}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onPress}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

export const MessageActionMenu = memo(
  ({
    children,
    timestampLabel,
    isPinned,
    onReply,
    onTogglePin,
    onForward,
    onReport,
  }: MessageActionMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const runAction = useCallback((action: () => void) => {
      setIsOpen(false);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          action();
        });
      });
    }, []);

    return (
      <Popover placement="bottom-start" offset={8} isOpen={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger>
          <div className="cursor-pointer">{children}</div>
        </PopoverTrigger>
        <PopoverContent className="bg-transparent p-0 shadow-none">
          <div className="min-w-[220px] rounded-[12px] bg-white p-2 shadow-[2px_4px_10px_rgba(141,147,178,0.25)]">
            <div className="flex flex-col gap-1">
              {onReply ? (
                <ActionItem
                  label="Reply"
                  icon={<IconReply className="h-[18px] w-[18px]" />}
                  onPress={() => runAction(onReply)}
                />
              ) : null}
              {onTogglePin ? (
                <ActionItem
                  label={isPinned ? 'Unpin message' : 'Pin message'}
                  icon={<IconPin className="h-[18px] w-[18px]" />}
                  onPress={() => runAction(onTogglePin)}
                />
              ) : null}
              {onForward ? (
                <ActionItem
                  label="Forward"
                  icon={<IconForward className="h-[18px] w-[18px]" />}
                  onPress={() => runAction(onForward)}
                />
              ) : null}
              {onReport ? (
                <ActionItem
                  label="Report"
                  icon={<IconFlag className="h-[18px] w-[18px]" />}
                  tone="danger"
                  onPress={() => runAction(onReport)}
                />
              ) : null}
            </div>
            <div className="mt-1 border-t border-black/10 px-3 pt-2 text-[11px] font-medium text-[#7A7A7A]">
              {timestampLabel}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);

MessageActionMenu.displayName = 'MessageActionMenu';
